/**
 * Função utilitária para fazer retry automático de requisições
 * @param {Function} requestFunction - Função que faz a requisição
 * @param {Array} args - Argumentos para passar para a função
 * @param {number} maxRetries - Número máximo de tentativas (padrão: 3)
 * @param {number} baseDelay - Delay base em ms (padrão: 1000)
 * @param {string} logPrefix - Prefixo para os logs (padrão: "Request")
 * @returns {Promise} Promise que resolve com o resultado ou rejeita após todas as tentativas
 */
export const retryRequest = async (
	requestFunction,
	args = [],
	maxRetries = 3,
	baseDelay = 1000,
	logPrefix = "Request"
) => {
	let lastError;

	for (let attempt = 0; attempt <= maxRetries; attempt++) {
		try {
			const result = await requestFunction(...args);
			return result;
		} catch (error) {
			lastError = error;
			console.error(`${logPrefix} - Error on attempt ${attempt + 1}/${maxRetries + 1}:`, error);
			if (attempt < maxRetries) {
				const delay = baseDelay * (attempt + 1);
				await new Promise(resolve => setTimeout(resolve, delay));
			}
		}
	}
	
	// Se chegou aqui, todas as tentativas falharam
	console.error(`${logPrefix} - All retry attempts failed`);
	throw lastError;
};

/**
 * Função para criar uma requisição com retry que pode ser usada em contextos
 * @param {Function} requestFunction - Função que faz a requisição
 * @param {Object} options - Opções de configuração
 * @param {number} options.maxRetries - Número máximo de tentativas (padrão: 3)
 * @param {number} options.baseDelay - Delay base em ms (padrão: 1000)
 * @param {string} options.logPrefix - Prefixo para os logs (padrão: "Request")
 * @returns {Function} Função que executa o request com retry
 */
export const createRetryFunction = (requestFunction, options = {}) => {
	const {
		maxRetries = 3,
		baseDelay = 1000,
		logPrefix = "Request"
	} = options;

	return async (...args) => {
		return await retryRequest(requestFunction, args, maxRetries, baseDelay, logPrefix);
	};
};

export default {
	retryRequest,
	createRetryFunction
};
